import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Caseload, Referral, UniqueMemberInfo, User, CareCoordinatorReferral } from 'types';

import EmptyReferrals from 'assets/empty_referral_list.png';
import Nav from 'components/_shared/NavSelector';
import ReferralCard from './components/ReferralCard';
import { ReferralFactory } from './factories/ReferralFactory';
import { UnifiedReferralModel } from './models/UnifiedReferralModel';
import { CTAButton3 } from 'components/_shared/buttons';

import { isObjectEmpty } from 'util/index';

import { Box, Grid, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    cardContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        margin: '24px auto 40px auto',
        maxWidth: '640px',
        width: '90%',
        '@media (max-width: 768px)': {
            maxWidth: '100%'
        }
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        margin: '48px auto',
        '& img': {
            maxWidth: '50%',
            height: 'auto'
        }
    },
    referralsBody: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '95%'
    },
    referralsPage: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '2rem',
        height: '100vh',
        overflow: 'auto'
    },
    referralsTitle: {
        paddingTop: '10px'
    },
    refreshContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        padding: '24px 0',
        '& span': {
            fontSize: '20px',
            fontWeight: 800,
            textAlign: 'center'
        }
    }
}));

type ReferralsTabProps = {
    acceptReferral: (referralID: number) => void;
    caseloads: Caseload[];
    fetchCaseloads: (userID: number) => void;
    fetchReferrals: (userID: number, orgID?: number) => void;
    fetchIncomingCareCoordinatorReferrals: (userID: number) => void;
    fetchOutgoingCareCoordinatorReferrals: (userID: number) => void;
    incomingReferrals: Referral[] | null;
    outgoingReferrals: Referral[] | null;
    incomingCareCoordinatorReferrals: CareCoordinatorReferral[] | null;
    outgoingCareCoordinatorReferrals: CareCoordinatorReferral[] | null;
    isFetchingCaseloads: boolean;
    isFetchingIncomingReferrals: boolean;
    isFetchingOutgoingReferrals: boolean;
    isFetchingIncomingCareCoordinatorReferrals: boolean;
    isFetchingOutgoingCareCoordinatorReferrals: boolean;
    uniqueMemberInfo: UniqueMemberInfo[] | null;
    rejectReferral: (referralID: number) => void;
    resetUserReferrals: () => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
    saveMemberToMyMembers: (
        caseloadID: number,
        memberID: number,
        accepted?: boolean
    ) => void;
    setLayoutTitles: (title: string, subtitle: string) => void;
    user: User;
};

type Direction = 'outgoing' | 'incoming';

const ReferralsTab: React.FC<ReferralsTabProps> = ({
    acceptReferral,
    caseloads,
    fetchCaseloads,
    fetchReferrals,
    fetchIncomingCareCoordinatorReferrals,
    fetchOutgoingCareCoordinatorReferrals,
    incomingReferrals,
    outgoingReferrals,
    incomingCareCoordinatorReferrals,
    outgoingCareCoordinatorReferrals,
    isFetchingCaseloads,
    isFetchingIncomingReferrals,
    isFetchingOutgoingReferrals,
    isFetchingIncomingCareCoordinatorReferrals,
    isFetchingOutgoingCareCoordinatorReferrals,
    uniqueMemberInfo,
    rejectReferral,
    resetUserReferrals,
    removeMemberFromMyMembers,
    saveMemberToMyMembers,
    setLayoutTitles,
    user
}) => {
    const classes = useStyles();
    const location = useLocation();

    const hasRefreshedCaseloads = useRef(false);

    const [active, setActive] = useState('Incoming Referrals');
    const [isDelayedVisible, setIsDelayedVisible] = useState(false);

    const direction: Direction = active === 'Outgoing Referrals' ? 'outgoing' : 'incoming';

    const matchHomelessToReferral = useCallback((referral: Referral | CareCoordinatorReferral) => {
        return (
            uniqueMemberInfo?.find(
                (homeless) => homeless.id === referral.homeless_id
            ) ?? null
        );
    }, [uniqueMemberInfo]);

    const matchHomelessToCaseload = useCallback((referral: Referral) => {
        return (
            caseloads?.find(
                (caseload) => caseload.homeless_id === referral.homeless_id
            ) ?? null
        );
    }, [caseloads]);

    const createUnifiedReferral = useCallback((referral: Referral | CareCoordinatorReferral): UnifiedReferralModel | null => {
        const homeless = uniqueMemberInfo?.find(
            (homeless) => homeless.id === referral.homeless_id
        );
        if (!homeless) return null;

        if ('is_active' in referral) {
            // Handle CareCoordinatorReferral
            return ReferralFactory.fromCareCoordinator(referral, {
                profileStage: homeless.profile_stage,
                user
            });
        } else {
            // Handle regular Referral
            const caseload = matchHomelessToCaseload(referral);
            return ReferralFactory.fromCaseManagement(referral, {
                profileStage: homeless.profile_stage,
                user,
                direction,
                caseloadReferralIDs: caseload?.referral_ids || [],
                caseloadUserID: caseload?.user_id
            });
        }
    }, [matchHomelessToCaseload, user, direction, uniqueMemberInfo]);

    const isLoading = useMemo(() => {
        return (
            !incomingReferrals ||
            !outgoingReferrals ||
            !incomingCareCoordinatorReferrals ||
            !outgoingCareCoordinatorReferrals ||
            !uniqueMemberInfo ||
            !user?.id ||
            isFetchingCaseloads ||
            isFetchingIncomingReferrals ||
            isFetchingOutgoingReferrals ||
            isFetchingIncomingCareCoordinatorReferrals ||
            isFetchingOutgoingCareCoordinatorReferrals
        );
    }, [
        incomingReferrals,
        outgoingReferrals,
        incomingCareCoordinatorReferrals,
        outgoingCareCoordinatorReferrals,
        uniqueMemberInfo,
        user?.id,
        isFetchingCaseloads,
        isFetchingIncomingReferrals,
        isFetchingOutgoingReferrals,
        isFetchingIncomingCareCoordinatorReferrals,
        isFetchingOutgoingCareCoordinatorReferrals
    ]);

    const navItems = ['Incoming Referrals', 'Outgoing Referrals'];

    const referralsToRender = useMemo(() => {
        const referrals = active === 'Incoming Referrals'
            ? [...(incomingReferrals || []), ...(incomingCareCoordinatorReferrals || [])]
            : [...(outgoingReferrals || []), ...(outgoingCareCoordinatorReferrals || [])];
        
        return referrals.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }, [active, incomingReferrals, outgoingReferrals, incomingCareCoordinatorReferrals, outgoingCareCoordinatorReferrals]);

    const fetchCaseloadsCallback = useCallback(
        () => fetchCaseloads(user?.id),
        [fetchCaseloads, user?.id]
    );

    const fetchReferralsCallback = useCallback(
        (userID: number, partnerID?: number) =>
            fetchReferrals(userID, partnerID),
        [fetchReferrals]
    );

    const resetReferralsCallback = useCallback(() => {
        if (user?.id) {
            fetchReferralsCallback(user?.id);
            fetchIncomingCareCoordinatorReferrals(user?.id);
            fetchOutgoingCareCoordinatorReferrals(user?.id);
        }

        if (user.partner?.id) {
            fetchReferralsCallback(user?.id, user?.partner?.id);
        }

        resetUserReferrals();
    }, [
        fetchReferralsCallback,
        fetchIncomingCareCoordinatorReferrals,
        fetchOutgoingCareCoordinatorReferrals,
        resetUserReferrals,
        user?.id,
        user.partner?.id
    ]);

    const ReferralSkeletonLayout = () => {
        return (
            <Box>
                <Grid container spacing={4}>
                    {[0, 1, 2].map((index) => (
                        <Grid item key={index} xs={12}>
                            <Skeleton variant="rounded" height={300} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    useEffect(() => {
        if (
            (!caseloads && !isFetchingCaseloads) ||
            (!hasRefreshedCaseloads.current && !isFetchingCaseloads)
        ) {
            fetchCaseloadsCallback();

            hasRefreshedCaseloads.current = true;
        }
    }, [caseloads, fetchCaseloadsCallback, isFetchingCaseloads]);

    useEffect(() => {
        if (user?.id) {
            fetchReferralsCallback(user?.id);
            fetchIncomingCareCoordinatorReferrals(user?.id);
            fetchOutgoingCareCoordinatorReferrals(user?.id);
        }

        if (user.partner?.id) {
            fetchReferralsCallback(user?.id, user?.partner?.id);
        }

        return () => {
            resetReferralsCallback();
        };
    }, [
        location.key,
        user?.id,
        user?.partner?.id,
        fetchReferralsCallback,
        fetchIncomingCareCoordinatorReferrals,
        fetchOutgoingCareCoordinatorReferrals,
        resetReferralsCallback
    ]);

    useEffect(() => {
        if (!isObjectEmpty(user)) {
            setLayoutTitles('', 'Referrals');
        }
    }, [setLayoutTitles, user]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (!isLoading) {
            timeout = setTimeout(() => setIsDelayedVisible(true), 500);
        } else {
            setIsDelayedVisible(false);
        }

        return () => clearTimeout(timeout);
    }, [isLoading]);

    return (
        <div className={classes.referralsPage}>
            <Helmet title="Referrals" />
            <h1 className={classes.referralsTitle}>Your Referrals</h1>
            <div className={classes.referralsBody}>
                <Nav
                    active={active}
                    navItems={navItems}
                    setActive={setActive}
                />
                {isLoading || !isDelayedVisible ? (
                    <div className={classes.cardContainer}>
                        <ReferralSkeletonLayout />
                    </div>
                ) : (
                    <div className={classes.cardContainer}>
                        {(referralsToRender.length ?? 0) > 0 ? (
                            referralsToRender?.map((referral, index) => {
                                const unifiedReferral = createUnifiedReferral(referral);
                                const homeless = matchHomelessToReferral(referral);
                                const caseload = unifiedReferral?.type === 'case_management' ? matchHomelessToCaseload(referral as Referral) : null;

                                if (!unifiedReferral || !homeless) return null;

                                return (
                                    <ReferralCard
                                        key={`${index}-${referral.id}`}
                                        acceptReferral={acceptReferral}
                                        caseload={caseload || undefined}
                                        homeless={homeless}
                                        profile={false}
                                        referral={unifiedReferral}
                                        rejectReferral={rejectReferral}
                                        removeMemberFromMyMembers={removeMemberFromMyMembers}
                                        saveMemberToMyMembers={saveMemberToMyMembers}
                                    />
                                );
                            })
                        ) : (
                            <div className={classes.empty}>
                                <img
                                    alt="Empty Referrals"
                                    src={EmptyReferrals}
                                />
                                <div className={classes.refreshContainer}>
                                    <span>
                                        You currently don't have any {direction}{' '}
                                        referrals.
                                    </span>
                                    <CTAButton3
                                        onClick={() => resetReferralsCallback()}
                                    >
                                        Refresh
                                    </CTAButton3>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReferralsTab;
